import { AutoSizer, List } from 'react-virtualized';
import { Set as ImmutableSet } from 'immutable';
import { Link, Route } from 'react-router-dom';
import { animateScroll } from 'react-scroll';
import { connect } from 'react-redux';
import Moment from 'react-moment';
import PropTypes from 'prop-types';
import React from 'react';
import Url from 'url';
import classNames from 'classnames';
import moment from 'moment-timezone';

import AnimatedRoutes, { absolute } from '../../components/AnimatedRoutes';
import { FloatingModal } from '../../components/Modal';
import {
  addActivity,
  addToFilter,
  getEvent,
  removeActivity,
  removeFromFilter,
} from '../../../../ducks/event';
import { addNotification, removeNotification } from '../../../../ducks/notifications';
import { getImageUrl, navigate } from '../../../../helpers';
import { isMobile, notificationPermission } from '../../../../mobile/helpers';
import { update } from '../../../../ducks/app';
import Activity from './Activity';
import Button from '../../components/Button';
import Content from '../../components/Content';
import Empty from '../../components/Empty';
import Filter from './Filter';
import Flash from '../../components/Flash';
import Header from '../../components/Header';
import Loader from '../../components/Loader';
import Maps from '../Maps';
import TimeSimulator from '../../components/TimeSimulator';
import activityStyles from './Activity/activity.scss';
import styles from './schedule.scss';

const AbsoluteMap = absolute(Maps, true);
const ROW_HEIGHT = 196;

const activityState = (activity) => {
  if (moment(activity.time_end).diff(moment()) < 0) {
    return -1; // passed
  }

  return 0; // active and upcoming
};

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    shortcode: PropTypes.string,
    local_start_time: PropTypes.string,
    local_end_time: PropTypes.string,
    date_start: PropTypes.string,
    date_end: PropTypes.string,
    timezone: PropTypes.string,
    activities: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
    })),
    tags: PropTypes.arrayOf(ImmutableSet),
    separate_schedule: PropTypes.arrayOf(PropTypes.string),
  }),
  personalActivities: PropTypes.instanceOf(ImmutableSet),
  filterTags: PropTypes.instanceOf(ImmutableSet),
  onRequestOpenFn: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  match: PropTypes.shape({
    params: PropTypes.shape({
      shortcode: PropTypes.string.isRequired,
      id: PropTypes.string,
    }).isRequired,
    url: PropTypes.string,
    path: PropTypes.string,
  }).isRequired,
  notifications: PropTypes.arrayOf(PropTypes.shape),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    stackPop: PropTypes.func.isRequired,
  }).isRequired,
  ignoreActivityNotifications: PropTypes.bool,
  debug: PropTypes.bool,
  simulatedTime: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

class Schedule extends React.Component {
  constructor(props) {
    super(props);

    const localTimezone = moment.tz(moment.tz.guess());

    this.state = {
      activeTab: 'all',
      filterModalOpen: false,
      updates: 0,
      localTimezone: localTimezone.zoneAbbr(),
    };

    // Activate simulated time before first render
    // (to be able to test the default selected day)
    const { debug, simulatedTime } = props;
    if (debug && simulatedTime) {
      moment.now = () => simulatedTime * 1000;
    }

    this.scrollTop = 0;
  }

  componentDidMount() {
    const { event } = this.props;
    if (event && event.activities) {
      this.scrollToActive();
    }
    // Update state every minute to rerender the view, otherwise date-sensitive
    // content will get stale (e.g. if an activity has already passed)
    this.interval = setInterval(
      () => this.setState((state) => ({ updates: state.updates + 1 })),
      60000,
    );
  }

  static getDerivedStateFromProps(props, state) {
    const {
      event,
      personalActivities,
      ignoreActivityNotifications,
      location,
    } = props;
    const { activeDay } = state;
    const ret = {
      hideDays: false,
      hideTabs: false,
      forcedFilterTags: null,
      activeDay,
    };

    if (!ret.activeDay) {
      ret.activeDay = moment().format('YYYY-MM-DD');
    }

    if (!ret.activeDay || (event
      && !moment(ret.activeDay).isBetween(event.local_start_time, event.local_end_time, 'date')
      && moment(event.local_start_time).format('YYYY-MM-DD') !== ret.activeDay
      && moment(event.local_end_time).format('YYYY-MM-DD') !== ret.activeDay
    )) {
      // Make sure we have an initial active day selected
      ret.activeDay = event
        && event.local_start_time
        && event.local_end_time
        && moment(event.local_start_time).format('YYYY-MM-DD');
    }

    if (isMobile() && personalActivities.size > 0 && !ignoreActivityNotifications) {
      notificationPermission().then((granted) => {
        if (!granted) {
          ret.notificationsMessage = 'Turn notifications on in your system settings if you want to receive notifications for activites that you add to your personal schedule.';
        }
      });
    }

    if (location.search) {
      const url = Url.parse(location.search, true);
      if (url.query.day) {
        ret.activeDay = moment(url.query.day).format('YYYY-MM-DD');
        ret.hideDays = true;
        ret.hideTabs = true;
      }
      if (url.query.tags) {
        ret.forcedFilterTags = url.query.tags.toLowerCase().split(',');
      }
    }

    return ret;
  }

  componentDidUpdate(oldProps) {
    const { event } = oldProps;
    const { event: newEvent } = this.props;
    if ((!event && newEvent) || (newEvent && event.activities !== newEvent.activities)) {
      this.scrollToActive();
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  onAddNotification = (activity, ignoreFail) => {
    const { dispatch } = this.props;
    return dispatch(addNotification({
      id: activity.id,
      type: 'activity',
      offset: 5,
      ignoreFail,
    }));
  }

  onRemoveNotification = (activity) => {
    const { dispatch } = this.props;
    return dispatch(removeNotification(activity.id));
  }

  onToggleNotification = (activity, ignoreFail) => {
    const { notifications } = this.props;
    if (notifications[activity.id]) {
      this.onRemoveNotification(activity);
    } else {
      this.onAddNotification(activity, ignoreFail);
    }
  }

  onAddActivity = (activity) => {
    const { dispatch, notifications } = this.props;
    dispatch(addActivity(activity.id));

    // If there is no existing notification, add one by default
    if (!notifications[activity.id]) {
      this.onAddNotification(activity, true);
    }
  }

  onRemoveActivity = (activity) => {
    const { dispatch, notifications } = this.props;
    dispatch(removeActivity(activity.id));

    // If there is an existing notification, remove it by default
    if (notifications[activity.id]) {
      this.onRemoveNotification(activity);
    }
  }

  getActivities = () => {
    const { event, personalActivities, filterTags } = this.props;
    const { activeDay, forcedFilterTags } = this.state;

    const startScope = moment(activeDay).startOf('day');
    const endScope = moment(activeDay).endOf('day').add(3, 'hours');

    const activities = event && event.activities && event.activities
      .filter((activity) => {
        const isSameDay = moment(activity.time).isBetween(startScope, endScope);

        // Filter if tags are specified by the query-string
        if (forcedFilterTags) {
          const tags = activity.tags && activity.tags.map((tag) => tag.name);
          const hasTag = tags.some((tag) => forcedFilterTags.indexOf(tag.toLowerCase()) !== -1);
          return hasTag && isSameDay;
        }

        const tags = activity.tags && activity.tags.map((tag) => tag.id);
        const hasTag = filterTags.size > 0 ? tags.some((tag) => filterTags.has(tag)) : true;
        return hasTag && isSameDay;
      })
      .sort((a, b) => {
        const stateA = activityState(a);
        const stateB = activityState(b);

        if (stateA !== stateB) {
          return stateA - stateB;
        }

        return moment(a.time).diff(b.time);
      });

    if (this.state.activeTab === 'mine') {
      return activities && activities.filter((activity) => (
        personalActivities.has(activity.id)
      ));
    }

    if (this.state.activeTab !== 'all') {
      return activities && activities.filter(
        (activity) => activity.tags.find((tag) => tag.id === this.state.activeTab),
      );
    }

    // Exclude all activites that have a separate schedule
    return activities && activities.filter(
      (activity) => !event.separate_schedule
        || !activity.tags.find((tag) => event.separate_schedule.indexOf(tag.id) !== -1),
    );
  }

  showAll = () => (
    /* See below for why we do this and how it works! */
    this.setState({
      activeTab: 'all',
    }, () => {
      const { scrollTop } = this.state;
      if (scrollTop) {
        animateScroll.scrollTo(scrollTop, {
          containerId: 'activities',
          duration: 0,
          smooth: false,
        });
      }
    })
  )

  showTab = (tab) => () => {
    if (this.state.activeTab === 'all') {
      this.setState({
        activeTab: tab,
        /* Since we don't actually switch tabs but rather change content, let's save
           the scroll state so we can keep it there if you switch back */
        scrollTop: this.scrollTop,
      });
    } else {
      this.setState({
        activeTab: tab,
        /* Since we don't actually switch tabs but rather change content, let's save
           the scroll state so we can keep it there if you switch back */
        scrollTop: this.scrollTop,
      });
    }
  }

  openFilterModal = () => {
    this.setState({
      filterModalOpen: true,
    });
  }

  closeFilterModal = () => {
    this.setState({
      filterModalOpen: false,
    }, () => (
      this.scrollToActive()
    ));
  }

  openActivityLink = (link) => {
    if (window) {
      window.open(link);
    }
  }

  changeDay = (day) => {
    // Hack to stop any active animated scrolling
    // (react-scroll cancels scrolling on keydown)
    const stopEvent = new Event('keydown');
    if (document) {
      document.dispatchEvent(stopEvent);
    }

    this.setState({
      activeDay: day,
    }, () => (
      this.scrollToActive()
    ));
  }

  scrollToActive() {
    const { event } = this.props;
    const activities = event && event.activities && this.getActivities();

    const next = activities && activities
      .findIndex((activity) => (
        moment.utc(activity.time).isAfter(moment.utc())
      ));

    if (next >= 2) {
      animateScroll.scrollTo((next - 2) * ROW_HEIGHT, { // Scroll to the offset
        containerId: 'activities',
        smooth: true,
        offset: -95,
        ignoreCancelEvents: true,
      });
    }
  }

  render() {
    const {
      dispatch,
      event,
      personalActivities,
      onRequestOpenFn,
      loading,
      error,
      match,
      notifications,
      filterTags,
      history,
      location,
      debug,
    } = this.props;
    const {
      activeTab,
      filterModalOpen,
      activeDay,
      notificationsMessage,
      hideTabs,
      hideDays,
      forcedFilterTags,
      localTimezone,
    } = this.state;
    const {
      getActivities,
      openFilterModal,
      closeFilterModal,
      openActivityLink,
      onToggleNotification,
      changeDay,
      onAddActivity,
      onRemoveActivity,
    } = this;
    const activities = getActivities();
    const parentTags = event && event.tags && event.tags[0];

    // Get all event days
    const days = [];
    if (event) {
      let currentDate = moment(event.local_start_time);
      const stopDate = moment(event.local_end_time);
      while (currentDate.diff(stopDate, 'date') <= 0) {
        days.push(moment(currentDate).format('YYYY-MM-DD'));
        currentDate = moment(currentDate).add(1, 'days');
      }
    }

    // Handle active activities
    const activeActivity = match.params.id && event && event.activities
      && event.activities.find(
        (act) => act.id === match.params.id,
      );

    const activeActivityStart = activeActivity && moment.parseZone(activeActivity.time);
    const isNotSameTimeZone = activeActivityStart && activeActivityStart.utcOffset()
      !== activeActivityStart.tz(localTimezone).utcOffset();

    const isSameDayStart = activeActivity && moment(activeActivity.time).format('dddd') === moment.parseZone(activeActivity.time).format('dddd');
    const isSameDayEnd = activeActivity && moment(activeActivity.time_end).format('dddd') === moment.parseZone(activeActivity.time_end).format('dddd');

    const separateSchedule = (event && event.separate_schedule && event.separate_schedule
      .map((tagId) => parentTags.find((tag) => tag.id === tagId))
      .sort((a, b) => a.name.localeCompare(b.name))) || [];

    const separateScheduleTabs = separateSchedule && separateSchedule
      .map((tag) => ({
        title: tag.name,
        onClick: this.showTab(tag.id),
        active: activeTab === tag.id,
      }));

    return (
      <>
        <Header
          tabs={!hideTabs && [
            { title: 'Main', onClick: this.showAll, active: activeTab === 'all' },
            ...separateScheduleTabs,
            { title: 'My schedule', onClick: this.showTab('mine'), active: activeTab === 'mine' },
          ]}
          onMenuOpen={onRequestOpenFn}
          history={history}
        >
          Schedule
        </Header>
        {event && debug
        && (
        <TimeSimulator
          start={event.local_start_time}
          end={event.local_end_time}
          timezone={event.timezone}
        />
        )}
        { hideDays && (
          <div className={styles.currentDay}>
            {moment(activeDay).format('dddd - ll')}
          </div>
        )}
        { !hideDays
          && days
          && days?.length > 0
          && (
          <div
            className={styles.days}
          >
            { days.map((day) => (
              <div
                key={day}
                onClick={() => changeDay(day)}
                role="button"
                tabIndex={0}
                className={classNames(
                  styles.day,
                  { [styles.activeDay]: day === activeDay },
                )}
              >
                <div className={styles.weekday}>
                  {moment(day).format('ddd')}
                </div>
                <div className={styles.dayOfMonth}>
                  {moment(day).format('D')}
                </div>
              </div>
            ))}
          </div>
          )}
        <Content>
          <Loader loading={loading} />
          <Empty
            empty={!loading && (!activities || !activities?.length)}
            title={activeTab === 'mine' ? 'You haven\'t saved any activities.' : 'There are no activities yet.'}
            message={activeTab === 'mine'
              ? 'Add activities to your personal schedule by checking the circle next to an activity.'
              : 'Don\'t worry, come back later to see if we\'ve added any activities.'}
            button={activeTab === 'mine'
              ? {
                onClick: this.showAll,
                text: 'See all activities',
              }
              : undefined}
            error={error}
            onReload={() => dispatch(getEvent(match.params.shortcode))}
          />
          { parentTags
            && parentTags?.length > 0
            && !forcedFilterTags
            && (
            <Filter
              tags={parentTags}
              filterTags={filterTags}
              isOpen={filterModalOpen}
              onOpen={openFilterModal}
              onClose={closeFilterModal}
              onToggle={(tagId) => (
                filterTags.has(tagId)
                  ? () => dispatch(removeFromFilter(tagId))
                  : () => dispatch(addToFilter(tagId))
              )}
            />
            )}
          { activities && activities?.length > 0
            && (
            <div className={styles.activities}>
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    id="activities"
                    width={width}
                    height={height}
                    rowCount={activities && activities?.length}
                    rowHeight={ROW_HEIGHT}
                    style={{ flex: 1, paddingBottom: '32px' }}
                    onScroll={({ scrollTop }) => { this.scrollTop = scrollTop; }}
                    overscanRowCount={15}
                    rowRenderer={({
                      key,
                      index,
                      style,
                    }) => (
                      <Activity
                        key={key}
                        activity={activities[index]}
                        personalActivities={personalActivities}
                        onClick={() => navigate(history, `${match.url}/${activities[index].id}${location.search}`)}
                        hasNotification={!!notifications[activities[index].id]}
                        isPersonal={personalActivities.has(activities[index].id)}
                        onRemove={() => onRemoveActivity(activities[index])}
                        onAdd={() => onAddActivity(activities[index])}
                        onToggleNotification={
                          () => onToggleNotification(activities[index])
                        }
                        hasPersonal={personalActivities.has(activities[index].id)}
                        style={style}
                        push={history.push}
                        shortcode={event.shortcode}
                        hideNotification={activeTab !== 'mine'}
                        match={match}
                        localTimezone={localTimezone}
                        timezone={event.timezone}
                      />
                    )}
                  />
                )}
              </AutoSizer>
            </div>
            )}
          <FloatingModal
            isOpen={activeActivity && true}
            onRequestClose={() => history.stackPop()}
          >
            { activeActivity
              && (
              <div className={styles.activeActivity}>
                <div className={styles.title}>
                  {activeActivity.title}
                </div>
                { activeActivity.image_id
                  && (
                  <div
                    className={styles.image}
                    style={{ backgroundImage: `url(${getImageUrl(activeActivity.image_id)})` }}
                  />
                  )}

                <div className={styles.tags}>
                  { activeActivity.main_tag && (
                    <div className={activityStyles.mainTag}>
                      {activeActivity.main_tag.name}
                    </div>
                  )}
                  { activeActivity.tags && activeActivity.tags
                    .filter((tag) => tag.id !== activeActivity.main_tag.id)
                    .map((tag) => (
                      <div className={activityStyles.tag} key={tag.id}>
                        {tag.name}
                      </div>
                    ))}
                </div>
                <div className={styles.info}>
                  <strong>When?</strong>
                  {' '}
                  <Moment format={isNotSameTimeZone ? `LT [${localTimezone}]${isSameDayStart ? '' : ' · dddd'}` : 'LT · dddd'}>{activeActivity.time}</Moment>
                  {isNotSameTimeZone && (
                    <> / {moment.parseZone(activeActivity.time).format(`LT [${event.timezone}] · dddd`)}</>
                  )}
                </div>
                {activeActivity.time_end
                  && (
                  <div className={styles.info}>
                    <strong>Until?</strong>
                    {' '}
                    <Moment format={isNotSameTimeZone ? `LT [${localTimezone}]${isSameDayEnd ? '' : ' · dddd'}` : 'LT · dddd'}>{activeActivity.time_end}</Moment>
                    {isNotSameTimeZone && (
                      <> / {moment.parseZone(activeActivity.time_end).format(`LT [${event.timezone}] · dddd`)}</>
                    )}
                  </div>
                  )}
                {activeActivity.time_end
                  && (
                  <div className={styles.info}>
                    <strong>Duration?</strong>
                    {' '}
                    {moment.duration(
                      moment(activeActivity.time_end).diff(moment(activeActivity.time)),
                    ).humanize()}
                  </div>
                  )}
                { activeActivity.area && activeActivity.area.name
                  && (
                  <div className={styles.info}>
                    <strong>Where?</strong>
                    {' '}
                    {activeActivity.area.name}
                  </div>
                  )}
                <div className={styles.buttons}>
                  { activeActivity.link
                    && (
                    <Button
                      outline
                      onClick={() => openActivityLink(activeActivity.link)}
                    >
                      Read more
                    </Button>
                    )}
                  { activeActivity.area && activeActivity.area.pin
                    && (
                    <Button>
                      <Link to={`${match.url}/map/${activeActivity.id}`}>
                        Show on map
                      </Link>
                    </Button>
                    )}
                </div>
                { !!activeActivity.accelerated && (
                  <div className={styles.accelerated}>
                    <i className="fad fa-exclamation" /> This activity is on an accelerated schedule, meaning it can start earlier based on preceding happenings.
                  </div>
                )}
              </div>
              )}
          </FloatingModal>
          <Flash
            persist
            neutral
            bottom
            title="Notifications turned off"
            message={notificationsMessage}
            onClose={() => dispatch(update({ ignoreActivityNotifications: true }))}
          />
        </Content>
        <AnimatedRoutes location={location}>
          <Route exact path={`${match.path}/map/:activity`} render={(props) => (<AbsoluteMap {...props} />)} />
        </AnimatedRoutes>
      </>
    );
  }
}

Schedule.propTypes = propTypes;


function mapStateToProps(state) {
  return {
    event: state.getIn(['event', 'data']),
    error: state.getIn(['event', 'error']),
    loading: state.getIn(['event', 'loading']),
    personalActivities: state.getIn(['event', 'activities']),
    tickets: state.getIn(['event', 'tickets']),
    filterTags: state.getIn(['event', 'filterTags']),
    notifications: state.getIn(['notifications', 'notifications']),
    ignoreActivityNotifications: state.getIn(['app', 'ignoreActivityNotifications']),
    debug: state.getIn(['app', 'debug']),
    simulatedTime: state.getIn(['app', 'simulatedTime']),
  };
}

export default connect(mapStateToProps)(Schedule);
